import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  Method,
} from 'axios';
import { HttpError } from './exceptions';
import { OnHttpRequest, OnHttpResponse } from './interceptors';

export class HttpClient {
  private readonly client: AxiosInstance;
  private readonly resource: string;

  constructor(
    resource: string,
    baseURL: string = import.meta.env.VITE_API_URL,
    config: AxiosRequestConfig = {},
  ) {
    this.client = axios.create({
      baseURL,
      ...config,
    });
    this.resource = resource;
  }

  async get<T>(options?: {
    url?: string;
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    return this.request(
      'GET',
      this.qs(options?.url ?? '', options?.query),
      options?.config,
    );
  }

  async post<T>(
    options?: {
      url?: string;
      query?: Record<string, any>;
      body?: any;
    },
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request('POST', this.qs(options?.url ?? '', options?.query), {
      data: options?.body ?? {},
      ...config,
    });
  }

  async put<T>(url = '', body = {}, config?: AxiosRequestConfig): Promise<T> {
    return this.request('PUT', this.qs(url), {
      data: body,
      ...config,
    });
  }

  async patch<T>(url = '', body = {}, config?: AxiosRequestConfig): Promise<T> {
    return this.request('PATCH', this.qs(url), {
      data: body,
      ...config,
    });
  }

  async delete<T>(
    url = '',
    query = {},
    config?: AxiosRequestConfig,
  ): Promise<T> {
    return this.request('DELETE', this.qs(url, query), config);
  }

  protected setOnRequest(onRequest: OnHttpRequest) {
    this.client.interceptors.request.use(
      function onFullFilled(config) {
        return onRequest(config, config.headers);
      },
      function onError(error) {
        return Promise.reject(error);
      },
    );

    return this;
  }

  protected setOnResponse(onResponse: OnHttpResponse) {
    this.client.interceptors.response.use(
      (success) => {
        onResponse.success?.(success.data, success.headers);
        return Promise.resolve(success);
      },
      (error: AxiosError) => {
        const httpError = new HttpError({
          statusCode: error.response?.status,
          message: error.message,
          data: error.response?.data,
        });
        onResponse.error?.(httpError, error.response?.headers);
        return Promise.reject(httpError);
      },
    );

    return this;
  }

  private qs(url: string, query: Record<string, any> = {}): string {
    const keyValues = Object.entries(query);
    const requestUrl = url.startsWith('/') ? url : `${this.resource}/${url}`;

    if (!keyValues.length) return requestUrl;

    const qs = keyValues
      .filter(([, value]) => !!value)
      .map((keyValue) => {
        const [key, v] = keyValue;

        if (Array.isArray(v)) {
          return v.map((value) => [key, value].join('=')).join('&');
        }

        return [key, v].join('=');
      })
      .filter(Boolean)
      .join('&');

    if (!qs) return requestUrl;

    const joinString = requestUrl.includes('?') ? '&' : '?';

    return [requestUrl, qs].join(joinString);
  }

  private async request<TValue, TError>(
    method: Method,
    url: string,
    config: AxiosRequestConfig = {},
  ): Promise<TValue> {
    try {
      const request = {
        method,
        url,
        ...config,
      };

      const result = await this.client.request(request);
      return result.data;
    } catch (error) {
      if (!(error as AxiosError).isAxiosError) throw error;

      const err = error as AxiosError<TError>;

      const { message, response } = err;
      const { status = 400, data } = response || {};

      throw new HttpError<TError>({
        message,
        statusCode: status,
        data,
      });
    }
  }
}
